import { TopNav } from './TopNav';
import { SiteFooter } from './SiteFooter';
import { PageGrid } from './PageGrid';
import { CtaSection } from './CtaSection';
import { ContentListPageHeader } from './ContentListPageHeader';
import { ContentListPagePanels } from './ContentListPagePanels';
import { ListPatternBody } from './ListPatternBody';
import { InfiniteListPatternBody } from './InfiniteListPatternBody';
import { panelMainClass } from '../lib/contentListPageLayout';
import { usesCoordinatedContentListReveal, resolveContentListPageChromeReady } from '../lib/contentListPageReveal';
import {
  wrapContentListBody,
  wrapContentListFooter,
} from '../lib/wrapContentListBody';
import {
  resolveContentListDataReady,
  type ContentListPageProps,
} from '../lib/contentListPageTypes';


function resolveBodyContent(
  props: ContentListPageProps,
  coordinatedReveal: boolean,
  hasPanel: boolean,
) {
  const listProps = 'listProps' in props ? props.listProps : undefined;
  const listState = 'listState' in props ? props.listState : undefined;
  const infiniteListProps = 'infiniteListProps' in props ? props.infiniteListProps : undefined;
  const infiniteListState = 'infiniteListState' in props ? props.infiniteListState : undefined;
  const loadMore = 'loadMore' in props ? props.loadMore : undefined;
  const children = 'children' in props ? props.children : undefined;

  if (infiniteListProps && infiniteListState && loadMore) {
    return (
      <InfiniteListPatternBody
        listProps={infiniteListProps}
        state={infiniteListState}
        loadMore={loadMore}
      />
    );
  }

  if (listProps && listState) {
    return <ListPatternBody listProps={listProps} state={listState} />;
  }

  if (children == null || children === false) {
    return null;
  }

  if (coordinatedReveal) {
    return children;
  }

  return wrapContentListBody(children, { hasPanel, stack: true });
}

export function ContentListPage(props: ContentListPageProps) {
  const { title, subtitle, leftPanel, rightPanel, footer, showCta, showFooter, contentReady } = props;
  const listState = 'listState' in props ? props.listState : undefined;
  const infiniteListState = 'infiniteListState' in props ? props.infiniteListState : undefined;

  const listContentReady = resolveContentListDataReady(listState, infiniteListState);

  const hasLeftPanel = leftPanel != null;
  const hasRightPanel = rightPanel != null;
  const hasPanel = hasLeftPanel || hasRightPanel;
  const coordinatedReveal = usesCoordinatedContentListReveal(hasPanel, contentReady);
  const bodyContent = resolveBodyContent(props, coordinatedReveal, hasPanel);

  const pageChromeReady = resolveContentListPageChromeReady({
    coordinatedReveal,
    contentReady,
    listContentReady,
    hasListState: listState != null || infiniteListState != null,
    hasBodyContent: Boolean(bodyContent),
  });

  const shouldShowCta = showCta ?? pageChromeReady;
  const shouldShowFooter = showFooter ?? showCta ?? pageChromeReady;

  return (
    <div className="min-h-screen bg-surface-base text-text-primary antialiased">
      <TopNav />

      <PageGrid className={hasPanel ? '' : 'pt-30'}>
        {hasPanel ? (
          coordinatedReveal ? (
            <ContentListPagePanels
              contentReady={contentReady ?? false}
              title={title}
              subtitle={subtitle}
              leftPanel={leftPanel}
              rightPanel={rightPanel}
              bodyContent={bodyContent}
              hasLeftPanel={hasLeftPanel}
              hasRightPanel={hasRightPanel}
            />
          ) : (
            <div className="content-list-panels col-span-full">
              {hasLeftPanel && (
                <div className="content-list-panel-left hidden shrink-0 self-stretch lg:block">
                  <div className="sticky top-20 pt-30">{leftPanel}</div>
                </div>
              )}
              <div className={panelMainClass(hasLeftPanel, hasRightPanel)}>
                <ContentListPageHeader title={title} subtitle={subtitle} />
                {bodyContent}
              </div>
              {hasRightPanel && (
                <div className="content-list-panel-right hidden shrink-0 self-stretch lg:block">
                  <div className="sticky top-20 pt-30">{rightPanel}</div>
                </div>
              )}
            </div>
          )
        ) : listState != null || infiniteListState != null ? (
          <div className="col-span-full flex flex-col gap-10">
            <ContentListPageHeader
              title={title}
              subtitle={subtitle}
              showTitle={Boolean(title)}
            />
            {bodyContent}
          </div>
        ) : (
          <>
            <div className="col-span-full">
              <ContentListPageHeader
                title={title}
                subtitle={subtitle}
                showTitle={Boolean(title)}
              />
            </div>
            {bodyContent}
          </>
        )}
      </PageGrid>

      {footer && (contentReady === undefined || contentReady) && (
        <PageGrid className="pt-60">
          <div className="col-span-full">
            {coordinatedReveal ? footer : wrapContentListFooter(footer)}
          </div>
        </PageGrid>
      )}

      {shouldShowCta && (
        <div className="pt-60">
          <CtaSection />
        </div>
      )}

      {shouldShowFooter && <SiteFooter />}
    </div>
  );
}
